import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, EmptyState, Skeleton, StatusBadge, useLocale, useToast } from '@ministry/ui/internal';

import { listOrgUnits, type OrgUnit } from '../../lib/admin-org.js';
import { orgUnitKindLabel, orgUnitStatusEntry } from '../../lib/admin-lifecycle.js';
import { ApiError } from '../../lib/api-client.js';
import { useT } from '../../i18n/strings.js';
import { PageHeader } from '../../components/PageHeader.js';
import { ForbiddenState } from '../../components/ForbiddenState.js';
import { CreateOrgUnitModal, RenameOrgUnitModal, ArchiveOrgUnitModal, CHILD_KIND } from './OrgUnitModals.js';

interface TreeNode extends OrgUnit {
  children: TreeNode[];
}

function buildTree(units: OrgUnit[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(units.map((unit) => [unit.id, { ...unit, children: [] }]));
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortByCode = (a: TreeNode, b: TreeNode) => a.code.localeCompare(b.code);
  const sortTree = (nodes: TreeNode[]) => {
    nodes.sort(sortByCode);
    nodes.forEach((n) => sortTree(n.children));
  };
  sortTree(roots);
  return roots;
}

/**
 * SCREEN_SPECS.md I20 — the ministry → division → section tree. Loading/error/empty are
 * handled here directly (rather than `DataTable`) because a tree has no natural row
 * count for pagination and the populated state is a nested list, not a table.
 */
export function OrgUnitsPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [createParent, setCreateParent] = React.useState<{ id: string; kind: OrgUnit['kind']; label: string } | null | undefined>(undefined);
  const [renameTarget, setRenameTarget] = React.useState<OrgUnit | null>(null);
  const [archiveTarget, setArchiveTarget] = React.useState<OrgUnit | null>(null);

  const query = useQuery({
    queryKey: ['admin-org-units', includeArchived],
    queryFn: ({ signal }) => listOrgUnits(includeArchived, signal),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin-org-units'] });

  if (query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('admin.org.title')} />
        <ForbiddenState error={query.error} />
      </div>
    );
  }

  const tree = query.data ? buildTree(query.data) : [];

  /**
   * R-049: `org_unit_tenant_single_ministry_key` allows exactly ONE live ministry node
   * per tenant, so "New top-level unit" is only ever a legitimate action on a ministry
   * that has not been given its root yet — in practice, the genuine first-run state the
   * empty view below describes. Offering it afterwards offered an operation the database
   * refuses by construction, and the refusal was reported as a duplicate CODE, so the
   * operator changed the code and failed again. A control that cannot succeed should not
   * be on the screen (DESIGN_SYSTEM.md §5's rule for nav, applied to actions).
   */
  const hasLiveMinistryRoot = (query.data ?? []).some(
    (unit) => unit.kind === 'ministry' && unit.archivedAt === null,
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('admin.org.title')}
        description={t('admin.org.description')}
        actions={
          hasLiveMinistryRoot ? undefined : (
            <Button variant="primary" onClick={() => setCreateParent(null)}>
              {t('admin.org.newMinistry')}
            </Button>
          )
        }
      />

      <Checkbox
        id="admin-org-include-archived"
        label={t('admin.org.includeArchived')}
        checked={includeArchived}
        onCheckedChange={(checked) => setIncludeArchived(checked === true)}
      />

      {query.isLoading ? (
        <Skeleton shape="list-row" count={6} />
      ) : query.isError ? (
        <EmptyState
          title={t('admin.org.error.title')}
          body={t('admin.org.error.body')}
          action={
            <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
              {t('admin.common.retry')}
            </Button>
          }
        />
      ) : tree.length === 0 ? (
        <EmptyState title={t('admin.org.empty.title')} body={t('admin.org.empty.body')} />
      ) : (
        <ul className="flex flex-col gap-1" role="tree" aria-label={t('admin.org.title')}>
          {tree.map((node) => (
            <OrgUnitRow
              key={node.id}
              node={node}
              locale={locale}
              t={t}
              onAddChild={(parent) => setCreateParent(parent)}
              onRename={setRenameTarget}
              onArchive={setArchiveTarget}
            />
          ))}
        </ul>
      )}

      <CreateOrgUnitModal
        open={createParent !== undefined}
        onOpenChange={(open) => {
          if (!open) setCreateParent(undefined);
        }}
        parent={createParent ?? null}
        onSuccess={(unit) => {
          invalidate();
          push({ variant: 'success', title: t('admin.org.toast.created', { name: locale === 'ne' ? unit.nameNe : unit.nameEn }) });
        }}
      />
      <RenameOrgUnitModal
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
        unit={renameTarget}
        onSuccess={(unit) => {
          invalidate();
          push({ variant: 'success', title: t('admin.org.toast.renamed', { name: locale === 'ne' ? unit.nameNe : unit.nameEn }) });
        }}
      />
      <ArchiveOrgUnitModal
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
        unit={archiveTarget}
        onSuccess={(unit) => {
          invalidate();
          push({ variant: 'success', title: t('admin.org.toast.archived', { name: locale === 'ne' ? unit.nameNe : unit.nameEn }) });
        }}
      />
    </div>
  );
}

function OrgUnitRow({
  node,
  locale,
  t,
  onAddChild,
  onRename,
  onArchive,
  depth = 0,
}: {
  node: TreeNode;
  locale: 'en' | 'ne';
  t: (key: string, vars?: Record<string, string | number>) => string;
  onAddChild: (parent: { id: string; kind: OrgUnit['kind']; label: string }) => void;
  onRename: (unit: OrgUnit) => void;
  onArchive: (unit: OrgUnit) => void;
  depth?: number;
}): React.JSX.Element {
  const label = locale === 'ne' ? node.nameNe : node.nameEn;
  const childKind = CHILD_KIND[node.kind];
  const isArchived = node.archivedAt !== null;

  return (
    <li role="treeitem" aria-expanded={node.children.length > 0} style={{ marginLeft: `${depth * 1.25}rem` }}>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-border-decorative p-2">
        <span className="text-xs font-medium uppercase text-text-muted">{orgUnitKindLabel(node.kind, locale)}</span>
        <span className="font-semibold text-text-primary">{label}</span>
        <span className="text-sm text-text-muted">({node.code})</span>
        <StatusBadge entry={orgUnitStatusEntry(node.archivedAt)} />
        <div className="ml-auto flex gap-2">
          {childKind && !isArchived ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onAddChild({ id: node.id, kind: node.kind, label })}
            >
              {t('admin.org.addChild', { kind: orgUnitKindLabel(childKind, locale) })}
            </Button>
          ) : null}
          {!isArchived ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => onRename(node)}>
                {t('admin.org.rename.action')}
              </Button>
              <Button variant="destructive" size="sm" onClick={() => onArchive(node)}>
                {t('admin.org.archive.action')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {node.children.length > 0 ? (
        <ul className="mt-1 flex flex-col gap-1">
          {node.children.map((child) => (
            <OrgUnitRow
              key={child.id}
              node={child}
              locale={locale}
              t={t}
              onAddChild={onAddChild}
              onRename={onRename}
              onArchive={onArchive}
              depth={depth + 1}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
